import { useFormikContext } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import useAuthorization from '../../../../../hooks/useAuthorization'
import { PERMISSION_MAPPINGS } from '../../AddEditUserModal/utils'
import PermissionAlert from './PermissionAlert/PermissionAlert'
import PermissionRow from './PermissionRow/PermissionRow'

/**
 * Product permission switches of a system user, bound to the surrounding Formik form.
 */
function PermissionFields({ permissionLevels, permissionEntities, isOwner = false }) {
  const intl = useIntl()
  const { values, setFieldValue } = useFormikContext()
  const { isAuthorizedForDiscover } = useAuthorization()

  const permissionsByName = useMemo(() => {
    const result = {}
    permissionEntities.forEach((permission) => {
      result[permission.module_name] = permission
    })
    return result
  }, [permissionEntities])

  const allPermissionsOptions = useMemo(
    () =>
      permissionLevels.map((item) => ({
        value: item.id,
        label: intl.formatMessage({ id: `USERPERMISSION.${item.name}` }),
      })),
    [permissionLevels, intl],
  )

  const visibleMappings = useMemo(
    () => PERMISSION_MAPPINGS.filter((mapping) => isAuthorizedForDiscover || mapping.moduleName !== 'discover'),
    [isAuthorizedForDiscover],
  )

  const changePermission = useCallback(
    (permissionId, level) => setFieldValue('permissions', { ...values.permissions, [permissionId]: level }),
    [setFieldValue, values.permissions],
  )

  return (
    <>
      {isOwner && <PermissionAlert messageId="USERPERMISSION.ownerinfonopermission" />}

      {visibleMappings.map((mapping) => {
        const permission = permissionsByName[mapping.moduleName]
        if (!permission) {
          console.warn(`No permission loaded for ${mapping.moduleName}`)
          return null
        }

        return (
          <PermissionRow
            key={mapping.moduleName}
            mapping={mapping}
            permission={permission}
            levelOptions={allPermissionsOptions}
            value={values.permissions[permission.id]}
            isOwner={isOwner}
            onChange={changePermission}
          />
        )
      })}
    </>
  )
}

PermissionFields.propTypes = {
  permissionLevels: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })).isRequired,
  permissionEntities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      module_name: PropTypes.string,
      permission_options: PropTypes.arrayOf(PropTypes.number),
    }),
  ).isRequired,
  isOwner: PropTypes.bool,
}

export default React.memo(PermissionFields)
